import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { errorFromResponse } from '@/lib/errors';
import { idempotencyKeyFor } from '@/lib/http-core';
import { orderQueryKey } from './useOrderQuery';

/**
 * QA-599 [PG 확인 후 결제완료] — `POST /api/admin/v1/orders/{orderId}/pg-recover`(order #126, payment #28, 본문 없음).
 * 토스에서 결제 완료를 확인하고 결제완료로 바꾼다(고객결제중단 주문). 응답 직후엔 아직 결제대기일 수 있어 2~3초 뒤 상세를 다시 읽는다.
 * 409 = 대상 아님·토스 미완료·금액 불일치 등(BE error.message 그대로), 503 = 결제 서비스 연결 실패.
 */
export interface PgRecoverResult {
  pgStatus?: string | null;
  paymentKey?: string | null;
  /** 청구 통화 최소 단위(USD 면 센트). */
  approvedAmount?: number | null;
  currency?: string | null;
  approvedAt?: string | null;
  orderStatus?: string | null;
  paymentStatus?: string | null;
  displayStatus?: string | null;
}

export const PG_RECOVER_REFETCH_DELAY_MS = 2500;

export const usePgRecoverMutation = (orderId: string) => {
  const qc = useQueryClient();
  return useMutation<PgRecoverResult, Error>({
    mutationFn: async () => {
      const path = `/api/admin/v1/orders/${encodeURIComponent(orderId)}/pg-recover`;
      const res = await adminFetch(path, {
        method: 'POST',
        headers: { 'Idempotency-Key': idempotencyKeyFor('POST', path, {}) },
      });
      if (!res.ok) throw await errorFromResponse(res);
      const env = (await res.json()) as { data?: PgRecoverResult };
      return env.data ?? {};
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: orderQueryKey(orderId) });
      // 결제완료 반영이 비동기라 잠시 뒤 한 번 더.
      window.setTimeout(() => void qc.invalidateQueries({ queryKey: orderQueryKey(orderId) }), PG_RECOVER_REFETCH_DELAY_MS);
    },
  });
};

/** 오류 문구 — 409 는 BE 사유 그대로, 503 은 다시 시도 안내. */
export const pgRecoverErrorMessage = (e: unknown): string => {
  const err = e as { status?: number; message?: string } | null;
  if (err?.status === 503) return '결제 서비스에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  if (err?.status === 409 && err.message) return err.message;
  return err?.message || 'PG 확인 후 결제완료 처리를 하지 못했습니다.';
};
